import { AnyObject } from '@triare/auth-redux';
import {
  DefaultFetchError,
  FetchGet,
  FetchSuccess,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchGet,
} from '../fetch';
import { User, UserRoleType, UserStatusType } from '../../store/auth';

// 'New registration', 'Family and friends', 'Confirmation email support user portal'
export const sendEmailOptions = [
  'Create account', 'Send auto login otp', 'Reset password', 'System Mail Kundenportal 2.0',
] as const;
export type SendEmailType = typeof sendEmailOptions[number];
export type SendEmailTypeSingle = typeof sendEmailOptions[number] | 'Welcome email';
export enum SendEmailEnum {
  // 'NEW_REGISTRATION' = 'New registration',
  'CREATE_ACCOUNT' = 'Create account',
  'SEND_AUTO_LOGIN_OTP' = 'Send auto login otp',
  'RESET_PASSWORD' = 'Reset password',
  'WELCOME_EMAIL' = 'Welcome email',
  'SYSTEM_MAIL_2_0' = 'System Mail Kundenportal 2.0',
  // 'CONFIRMATION_EMAIL_SUPPORT_USER_PORTAL' = 'Confirmation email support user portal',
}

export const emailStatusOptions = ['not invited', 'sent', 'successful registration'] as const;
export type EmailStatusType = typeof emailStatusOptions[number];

export interface InviteUsersBulkProps {
  // limit?: number; // number; // TODO check. moved to SubmitProps interface
  tariffId?: number; // number;
  tariffName?: string;
  tariffNames?: string[];

  /** Those 2 fields only work together */
  emailType?: SendEmailType; /** Filters users by type of emails, which has specified in form emailStatus */
  emailStatus?: EmailStatusType; /** Filters by status users, who has received specified in form emailType */

  isContractPresent?: boolean;

  /** Regular user filters */
  email?: string;
  name?: string;
  roles?: UserRoleType[];
  status?: UserStatusType;
}

export interface InviteUsersSubmitProps extends InviteUsersBulkProps {
  limit: number;
  sendEmailType: SendEmailType;
}

/** Table request */
interface SentEmailsItem {
  type: SendEmailType;
  link: string;
  action: 'Manually send email' | string;
  createdAt: string;
}

export interface InvitationUserItem extends User {
  sentEmails: SentEmailsItem[];
}

interface UsersGetParams extends PagingParams, InviteUsersBulkProps {
  orderByColumn?: 'username' | 'firstName';
}

export const useUsersInviteGet = <DD = UsersInviteTableData>(
  decorateData?: (data: PagingDataResponse<InvitationUserItem>) => DD,
): FetchGet<
  PagingDataResponse<InvitationUserItem>,
  UsersGetParams,
  DefaultFetchError,
  DD
> => useFetchGet(
    'emails', // users/bulk-email-list
    {
      autoStart: false,
      startStateLoading: false,
      decorateData,
    },
  );

export interface UserInviteTableRow extends Omit<InvitationUserItem, 'settings'> {
  key: string;
  fullName: string;
}

export interface UsersInviteTableData {
  data: UserInviteTableRow[];
  total: number;
}

export const useUsersInviteTableGet = () => useUsersInviteGet<UsersInviteTableData>(
  ({ data, meta }: PagingDataResponse<InvitationUserItem>): UsersInviteTableData => ({
    data: data.map(({
      settings, ...item
    }: InvitationUserItem): UserInviteTableRow => ({
      key: item.id,
      fullName: `${item.firstName || '-'} ${item.lastName || '-'}`,
      ...item,
    })),
    total: meta.itemCount,
  }),
);

/** Invite multiple users */
export const useInviteUsersBulk = () => (
  useFetchCreate<FetchSuccess, DefaultFetchError, InviteUsersSubmitProps>('emails/bulk-distribution')
);

/** Invite single user */
export interface InviteSingleUserProps {
  type: SendEmailTypeSingle;
  userId: string;
  contractNumber?: string;
}
export const useInviteUser = () => (
  useFetchCreate<FetchSuccess, DefaultFetchError, InviteSingleUserProps>('emails/send-one')
);

/** Invite emails queue */
export interface EmailsQueueStatus {
  group: string,
  done: number,
  total: number,
  type: SendEmailType,
}

export const useEmailsQueueStatusGet = (): FetchGet<
  EmailsQueueStatus[],
  UsersGetParams
> => useFetchGet(
  'emails/status',
  { autoStart: true, startStateLoading: false },
);

export type EmailQueueStatusType = 'pending' | 'in_progress' | 'done' | 'error';

export interface EmailsQueueItem {
  status: EmailQueueStatusType;
  group: string;
  type: SendEmailType;
  expirationDate: string;
  errorData: AnyObject;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface EmailsQueueGetParams extends PagingParams {
  type?: SendEmailType;
  status?: EmailQueueStatusType;
  group?: string;
}

export const useEmailsQueueGet = <DD = UsersInviteTableData>(
  decorateData?: (data: PagingDataResponse<EmailsQueueItem>) => DD,
): FetchGet<
  PagingDataResponse<EmailsQueueItem>,
  EmailsQueueGetParams,
  DefaultFetchError,
  DD
> => useFetchGet(
    'emails/queue', // users/bulk-email-list
    {
      autoStart: false,
      startStateLoading: false,
      decorateData,
    },
  );

export interface EmailsQueueTableRow extends Omit<EmailsQueueItem, 'settings'> {
  id: string;
  key: string;
  fullName: string;
}

export interface EmailsQueueTableData {
  data: EmailsQueueTableRow[];
  total: number;
}

export const useEmailsQueueTableGet = () => useEmailsQueueGet<EmailsQueueTableData>(
  ({ data, meta }: PagingDataResponse<EmailsQueueItem>): EmailsQueueTableData => ({
    data: data.map(({
      ...item
    }: EmailsQueueItem): EmailsQueueTableRow => ({
      key: item.user.id,
      id: item.user.id,
      fullName: `${item.user.firstName || '-'} ${item.user.lastName || '-'}`,
      ...item,
    })),
    total: meta.itemCount,
  }),
);
